<template>
  <main class="wallet-bill-detail-page" :class="{ 'is-asset-mode': !hasTransferMeta }">
    <AppNavBar :title="pageTitle" />

    <section v-if="trade" class="detail-panel">
      <div v-if="hasTransferMeta && coinImage" class="detail-icon">
        <app-image :src="coinImage" alt="" />
      </div>
      <div v-else-if="hasTransferMeta" class="detail-icon detail-icon--fallback">
        {{ (trade.coin || trade.symbol || 'T').slice(0, 1).toUpperCase() }}
      </div>


      <div class="detail-list">
        <div class="detail-amount" :class="{ 'is-positive': direction === 'in' }">
        {{ hasTransferMeta ? `${direction === 'in' ? '+' : '-'} ${displayAmount} ${trade.coin || trade.symbol || ''}`.trim() : displayAssetAmount }}
      </div>
      <div v-if="hasTransferMeta && displayUsdtValue" class="detail-usdt">~${{ displayUsdtValue }}</div>

      <div v-if="hasTransferMeta && showStatus" class="detail-status" :class="`is-${statusKey}`">
        <span class="detail-status__icon">
          <van-icon :name="statusIcon" />
        </span>
        <div data-strong>{{ statusText }}</div>
      </div>
        <div v-if="!hasTransferMeta" class="detail-row">
          <span>{{ t('wallet.assetType') }}</span>
          <div data-strong>{{ trade.symbol || trade.coin || '--' }}</div>
        </div>
        <div v-if="!hasTransferMeta" class="detail-row">
          <span>{{ t('card.tradeDescription') }}</span>
          <div data-strong>{{ trade.logLevelMsg || trade.remark || '--' }}</div>
        </div>
        <div class="detail-row">
          <span>{{ t('wallet.billTimeLabel') }}</span>
          <div data-strong>{{ displayTime }}</div>
        </div>
        <div v-if="displayInAddress" class="detail-row">
          <span>{{ t('wallet.transferInAddress') }}</span>
          <div data-strong>{{ shortAddress(displayInAddress) }}</div>
          <button type="button" class="copy-btn" @click="copyText(displayInAddress)">
            <van-icon name="notes-o" />
          </button>
        </div>
        <div v-if="displayOutAddress" class="detail-row">
          <span>{{ t('wallet.transferOutAddress') }}</span>
          <div data-strong>{{ shortAddress(displayOutAddress) }}</div>
          <button type="button" class="copy-btn" @click="copyText(displayOutAddress)">
            <van-icon name="notes-o" />
          </button>
        </div>
        <div v-if="trade.hash" class="detail-row">
          <span>{{ t('wallet.transactionId') }}</span>
          <div data-strong>{{ shortHash }}</div>
          <button type="button" class="copy-btn" @click="copyText(String(trade.hash || ''))">
            <van-icon name="notes-o" />
          </button>
        </div>
        <div v-if="trade.chainName || trade.chainImg" class="detail-row">
          <span>{{ t('wallet.network') }}</span>
          <div data-strong>{{ trade.chainName || '--' }}</div>
          <app-image v-if="trade.chainImg" :src="trade.chainImg" alt="" class="network-icon" />
        </div>
      </div>

      <button v-if="trade.explorerUrl" type="button" class="browser-btn" @click="openExplorer">
        {{ t('wallet.blockchainBrowser') }}
      </button>
    </section>

    <section v-else class="empty-card">
      <AppEmptyState :description="t('wallet.noBillDetail')" />
    </section>
  </main>
</template>

<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { showFailToast, showToast } from 'vant';
import { showPassedToast } from '@/utils/toast';
import { useWalletBillStore } from '@/stores/wallet-bill';
import { copyToClipboard } from '@/utils/clipboard';
import { getRouteState } from '@/utils/route-state';
import { formatCardTimestamp } from '@/view/card/utils';
import { formatMoney, formatTokenAmount, shortAddress } from '@/utils/format';

const { t } = useI18n();
const route = useRoute();
const walletBillStore = useWalletBillStore();
const pageState = computed(() => getRouteState(route, 'wallet-bill-detail', {}));

function normalizeText(value) {
  if (value === null || value === undefined) {
    return '';
  }
  const text = String(value).trim();
  if (!text || text.toLowerCase() === 'null' || text.toLowerCase() === 'undefined') {
    return '';
  }
  return text;
}

function pickFirstValue(record, keys = []) {
  for (const key of keys) {
    const value = normalizeText(record?.[key]);
    if (value) {
      return value;
    }
  }
  return '';
}

function normalizeTradeRecord(record = {}) {
  const addOrSubAmount = pickFirstValue(record, ['addOrSubAmount', 'changeAmount', 'amountChange']) || '0';
  const amount = pickFirstValue(record, ['amount', 'addOrSubAmount', 'changeAmount', 'amountChange']) || '0';
  const type = pickFirstValue(record, ['type']) || (Number(addOrSubAmount) > 0 ? 'in' : 'out');

  return {
    ...record,
    symbol: pickFirstValue(record, ['symbol', 'coin']),
    coin: pickFirstValue(record, ['coin', 'symbol']),
    addOrSubAmount,
    amount,
    type,
    status: record?.status ?? '',
    createTime: record?.createTime || '',
    time: pickFirstValue(record, ['time', 'createTime', 'createTimeStr']),
    fromAddress: pickFirstValue(record, ['fromAddress', 'from', 'addressFrom', 'fromAddr', 'sender', 'sourceAddress']),
    toAddress: pickFirstValue(record, ['toAddress', 'to', 'addressTo', 'toAddr', 'receiver', 'targetAddress']),
    hash: pickFirstValue(record, ['hash', 'txHash', 'transactionHash', 'tradeHash']),
    chainName: pickFirstValue(record, ['chainName', 'network', 'networkName', 'symbolType']),
    chainImg: pickFirstValue(record, ['chainImg', 'networkImg', 'chainLogo', 'symbolTypeImg']),
    explorerUrl: pickFirstValue(record, ['explorerUrl', 'scanUrl', 'browserUrl', 'txUrl']),
    logLevelMsg: pickFirstValue(record, ['logLevelMsg', 'remark', 'detail']),
    usdtPrice: pickFirstValue(record, ['usdtPrice', 'price']) || '0',
    img: pickFirstValue(record, ['img', 'logo', 'symbolImg'])
  };
}

const routeTrade = computed(() => {
  if (!Object.keys(pageState.value || {}).length) {
    return null;
  }

  const normalized = normalizeTradeRecord(pageState.value);
  if (!normalized.symbol && !normalized.logLevelMsg && !normalized.addOrSubAmount && !normalized.createTime && !normalized.hash) {
    return null;
  }
  return normalized;
});

const trade = computed(() => {
  const currentTrade = walletBillStore.currentTrade || routeTrade.value;
  return currentTrade ? normalizeTradeRecord(currentTrade) : null;
});
const direction = computed(() => {
  const type = String(trade.value?.type || '').toLowerCase();
  if (type === 'in' || type === 'out') {
    return type;
  }
  return Number(trade.value?.addOrSubAmount || 0) > 0 ? 'in' : 'out';
});
const hasTransferMeta = computed(() => Boolean(
  normalizeText(trade.value?.fromAddress) ||
  normalizeText(trade.value?.toAddress) ||
  normalizeText(trade.value?.hash) ||
  normalizeText(trade.value?.chainName) ||
  normalizeText(trade.value?.explorerUrl)
));
const pageTitle = computed(() => (hasTransferMeta.value ? t('wallet.transferRecord') : t('wallet.billDetailTitle')));
const showStatus = computed(() => {
  if (hasTransferMeta.value) {
    return true;
  }
  const status = trade.value?.status;
  return status !== null && status !== undefined && String(status).trim() !== '';
});
const amountValue = computed(() => Number(trade.value?.amount || trade.value?.addOrSubAmount || 0));
const displayAmount = computed(() => formatTokenAmount(Math.abs(amountValue.value), 8));
const displayAssetAmount = computed(() => {
  const amount = String(trade.value?.addOrSubAmount || '0').trim() || '0';
  const symbol = trade.value?.symbol || trade.value?.coin || '';
  const prefix = Number(amount) > 0 ? '+' : '';
  return `${prefix}${amount} ${symbol}`.trim();
});
const displayUsdtValue = computed(() => {
  const price = Number(trade.value?.usdtPrice || 0);
  if (!price) {
    return '';
  }
  const amount = Math.abs(Number(trade.value?.amount || amountValue.value || 0));
  return formatMoney(amount * price);
});
const coinImage = computed(() => String(trade.value?.img || '').trim());
const statusKey = computed(() => {
  if (!showStatus.value) {
    return '';
  }
  const status = Number(trade.value?.status);
  if (status === 1) {
    return 'success';
  }
  if (status === 0) {
    return 'pending';
  }
  return 'failed';
});
const statusText = computed(() => {
  if (statusKey.value === 'success') {
    return t('wallet.finish');
  }
  if (statusKey.value === 'pending') {
    return t('wallet.inTransaction');
  }
  return t('wallet.failed');
});
const statusIcon = computed(() => {
  if (statusKey.value === 'success') {
    return 'passed';
  }
  if (statusKey.value === 'pending') {
    return 'clock-o';
  }
  return 'close';
});
const displayTime = computed(() =>
  formatRecordTime(
    hasTransferMeta.value
      ? trade.value?.time || trade.value?.createTime
      : trade.value?.createTime,
  ) || '--',
);
const displayInAddress = computed(() => {
  if (!trade.value) {
    return '';
  }
  return String(direction.value === 'in' ? trade.value.fromAddress || '' : trade.value.toAddress || '');
});
const displayOutAddress = computed(() => {
  if (!trade.value) {
    return '';
  }
  return String(direction.value === 'in' ? trade.value.toAddress || '' : trade.value.fromAddress || '');
});
const shortHash = computed(() => {
  const hash = String(trade.value?.hash || '');
  if (!hash) {
    return '--';
  }
  if (hash.length <= 20) {
    return hash;
  }
  return `${hash.slice(0, 10)}...${hash.slice(-10)}`;
});

async function copyText(text) {
  if (!text) {
    return;
  }
  const copied = await copyToClipboard(String(text));
  if (copied) {
    showPassedToast(t('wallet.copySuccess'));
    return;
  }
  showFailToast(t('wallet.copyFailed'));
}

function openExplorer() {
  const url = String(trade.value?.explorerUrl || '').trim();
  if (!url) {
    return;
  }
  window.open(url, '_blank', 'noopener');
}

function formatRecordTime(value) {
  if (!value) {
    return '';
  }
  if (typeof value === 'string' && Number.isNaN(Number(value))) {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    const pad = (input) => String(input).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  }
  return formatCardTimestamp(value);
}
</script>

<style scoped lang="scss">
.wallet-bill-detail-page {
  min-height: var(--app-viewport-height);
  padding-top: 20px;
  background: #ffffff;

  &.is-asset-mode {
    background: #f7f7f7;
  }
}

.empty-card {
  background: #ffffff;
}

.detail-panel {
  background: #ffffff;

  .wallet-bill-detail-page.is-asset-mode & {
    padding: 0px 14px 0;
    background: transparent;
  }
}

.detail-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  margin: 0 auto;
  border-radius: 25px;
  overflow: hidden;
  background: #2667ff;

  .app-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.detail-icon--fallback {
  color: #ffffff;
  font-size: 20px;
  font-weight: 700;
}

.detail-amount {
  margin-top: 10px;
  color: #111111;
  font-size: 24px;
  font-weight: 700;
  text-align: center;

  .wallet-bill-detail-page.is-asset-mode & {
    margin-top: 0;
    color: #f53f3f;
    font-size: 28px;
  }

  .wallet-bill-detail-page.is-asset-mode &.is-positive {
    color: #07B077;
  }
}

.detail-usdt {
  margin-top: 4px;
  color: #999999;
  font-size: 14px;
  text-align: center;
}

.detail-status {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  margin-top: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid #eeeeee;

  [data-strong] {
    font-size: 14px;
    font-weight: 700;
  }

  &.is-success {
    color: #07b077;
  }

  &.is-pending {
    color: #9aa0a6;
  }

  &.is-failed {
    color: #ef0027;
  }

  &.is-success .detail-status__icon {
    background: #07b077;
  }

  &.is-pending .detail-status__icon {
    background: #9aa0a6;
  }

  &.is-failed .detail-status__icon {
    background: #ef0027;
  }
}

.detail-status__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 12px;
  color: #ffffff;
}

.detail-list {
  padding: 5px 15px;

  .wallet-bill-detail-page.is-asset-mode & {
    margin-top: 24px;
    padding: 18px 16px;
    border-radius: 16px;
    background: #ffffff;
    box-shadow: 0 8px 24px rgba(17, 17, 17, 0.04);
  }
}

.detail-row {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-height: 30px;
  gap: 10px;

  span {
    flex-shrink: 0;
    color: #999999;
    font-size: 14px;
  }

  [data-strong] {
    margin-left: auto;
    color: #333333;
    font-size: 14px;
    font-weight: 500;
    text-align: right;
    word-break: break-all;
  }

  .wallet-bill-detail-page.is-asset-mode & {
    min-height: 52px;
    justify-content: space-between;
    gap: 14px;

    & + .detail-row {
      border-top: 1px dashed #e5e6eb;
    }
  }
}

.copy-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: #999999;
}

.network-icon {
  width: 30px;
  height: 30px;
  border-radius: 15px;
  object-fit: cover;
}

.browser-btn {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  height: 56px;
  margin-top: 5px;
  padding: 0 15px;
  border: 0;
  border-top: 1px solid #eeeeee;
  border-bottom: 1px solid #eeeeee;
  background: #ffffff;
  color: #333333;
  font-size: 14px;
  font-weight: 500;
  text-align: left;
}

.empty-card {
  padding: 28px 16px;
}
</style>
